import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Globe,
  Key,
  Edit3,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import { login_success } from "../../actions/user";
import { getTenantFrontendUrl } from "../../utils/domain";

export default function Profile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user);

  const [profile, setProfile] = useState(reduxUser || null);
  const [loading, setLoading] = useState(true);

  // Fetch fresh profile from user/admin-profile and tenants/portal/profile
  const fetchProfile = useCallback(async () => {
    loader(true);
    setLoading(true);

    let adminData = {};
    let portalData = {};

    try {
      // 1. First fetch tenant admin personal profile endpoint
      const res = await ApiClient.get("user/admin-profile", {}, "", true);
      if (res?.success && (res?.data || res?.user_data)) {
        adminData = res.data || res.user_data;
      }
    } catch (err) {}

    try {
      // 2. Fetch tenant organization portal profile (brand colors, plan, address, contacts)
      const pRes = await ApiClient.get("tenants/portal/profile", {}, "", true);
      if (pRes?.success && (pRes?.data || pRes?.tenant || pRes?.profile)) {
        portalData = pRes.data || pRes.tenant || pRes.profile;
      }
    } catch (err) {}

    const isTenant =
      Boolean(adminData?.role?.name === "Tenant-Admin") ||
      Boolean(adminData?.tenantId) ||
      Boolean(portalData?.company_name) ||
      Boolean(portalData?.subdomain) ||
      Boolean(reduxUser?.company_name);

    const merged = {
      ...reduxUser,
      ...adminData,
      ...portalData,
      isTenantAdmin: isTenant,
    };

    setProfile(merged);
    dispatch(login_success(merged));
    loader(false);
    setLoading(false);
  }, [reduxUser, dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token && !reduxUser?._id && !reduxUser?.id) {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, []);

  const isTenantAdmin =
    profile?.isTenantAdmin ||
    profile?.role?.name === "Tenant-Admin" ||
    profile?.roleType === "tenant_admin" ||
    profile?.role === "tenant_admin" ||
    Boolean(profile?.company_name || profile?.subdomain);

  const fullName =
    profile?.fullName ||
    (profile?.firstName
      ? `${profile.firstName} ${profile.lastName || ""}`.trim()
      : "") ||
    profile?.name ||
    "Tenant Admin";

  const getSubdomainUrl = (sub) => {
    if (!sub) return "";
    return getTenantFrontendUrl(sub);
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px] bg-[#F4F7FB] py-8 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          {/* Top Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 bg-white p-5 rounded-2xl border border-slate-300 shadow-sm">
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                {isTenantAdmin ? "Tenant Administrator Profile" : "User Profile"}
              </h1>
              <p className="text-xs sm:text-sm text-slate-700 font-medium mt-0.5">
                Manage your credentials, organization metadata, and portal settings.
              </p>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <Link
                to="/changepassword"
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                <Key className="w-4 h-4 text-[#115B9D]" />
                <span>Change Password</span>
              </Link>
              <Link
                to="/editprofile"
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#0E528F] hover:from-[#0E528F] hover:to-[#08335C] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit Profile</span>
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            {/* Executive Identity Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 border-slate-300 p-1.5 bg-white shadow-2xs flex items-center justify-center overflow-hidden shrink-0">
                    {profile?.logo || profile?.logo_url || profile?.image ? (
                      <img
                        src={profile?.logo || profile?.logo_url || methodModel.userImg(profile?.image)}
                        alt={fullName}
                        className="w-full h-full object-contain rounded-xl"
                      />
                    ) : (
                      <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#115B9D] to-[#062037] flex items-center justify-center text-white text-2xl font-bold">
                        {fullName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 capitalize truncate">
                      {fullName}
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-700 font-semibold truncate">
                      {profile?.email}
                    </p>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-[#115B9D] border border-blue-300 text-[11px] font-bold uppercase tracking-wider mt-1">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {isTenantAdmin ? "Tenant Administrator" : profile?.roleName || "Customer"}
                    </div>
                  </div>
                </div>

                {isTenantAdmin && profile?.subdomain && (
                  <div className="pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-slate-200 sm:pl-5 flex flex-col justify-center shrink-0">
                    <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider block mb-1">
                      Dedicated Storefront
                    </span>
                    <a
                      href={getSubdomainUrl(profile.subdomain)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#F4F7FB] border border-slate-300 hover:border-[#115B9D] text-xs font-bold text-[#115B9D] transition-colors group shadow-2xs"
                    >
                      <span className="truncate max-w-[200px] sm:max-w-xs font-mono">{profile.subdomain}.frontend...</span>
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Organization Info Card (If Tenant Admin) */}
            {isTenantAdmin && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-5 sm:p-6 space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                  <Building2 className="w-4 h-4 text-[#115B9D]" />
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Organization &amp; Store Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Company Name</span>
                    <span className="text-sm font-black text-slate-900 truncate block">
                      {profile?.company_name || profile?.companyName || "--"}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Subdomain</span>
                    <span className="text-sm font-black text-[#115B9D] font-mono truncate block">
                      {profile?.subdomain || "--"}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Portal Title</span>
                    <span className="text-sm font-bold text-slate-900 truncate block">
                      {profile?.portal_title || "--"}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Support Email</span>
                    <span className="text-sm font-bold text-slate-900 truncate block">
                      {profile?.support_email || profile?.contact_email || profile?.email || "--"}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Support Phone</span>
                    <span className="text-sm font-bold text-slate-900 truncate block">
                      {profile?.support_phone || profile?.contact_phone || profile?.adminPhone || profile?.mobileNo || "--"}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                    <span className="text-xs text-slate-600 font-bold block">Business Address</span>
                    <span className="text-sm font-bold text-slate-900 block">
                      {typeof profile?.address === "object"
                        ? [
                            profile.address?.street,
                            profile.address?.city,
                            profile.address?.state,
                            profile.address?.zipCode,
                          ]
                            .filter(Boolean)
                            .join(", ") || "--"
                        : profile?.address || "--"}
                    </span>
                  </div>

                  {/* Active Plan */}
                  {profile?.plan && (
                    <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs sm:col-span-2 lg:col-span-3">
                      <span className="text-xs text-slate-600 font-bold block">
                        Active Plan Tier
                      </span>
                      <span className="text-sm font-black text-indigo-700">
                        {typeof profile.plan === "object" ? profile.plan.name : profile.plan}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Personal Administrator Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <User className="w-4 h-4 text-[#115B9D]" />
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Personal Information
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                  <span className="text-xs text-slate-600 font-bold block">Full Name</span>
                  <span className="text-sm font-black text-slate-900 capitalize truncate block">
                    {fullName}
                  </span>
                </div>

                <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                  <span className="text-xs text-slate-600 font-bold block">Login Email</span>
                  <span className="text-sm font-bold text-slate-900 truncate block">{profile?.email}</span>
                </div>

                <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                  <span className="text-xs text-slate-600 font-bold block">Mobile / Phone</span>
                  <span className="text-sm font-bold text-slate-900 truncate block">
                    {profile?.adminPhone || profile?.mobileNo || "--"}
                  </span>
                </div>

                <div className="bg-white rounded-xl p-3.5 border border-slate-300 shadow-2xs">
                  <span className="text-xs text-slate-600 font-bold block">Role</span>
                  <span className="text-sm font-bold text-slate-900 capitalize truncate block">
                    {isTenantAdmin ? "Tenant Administrator" : profile?.roleName || "Customer"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
